/**
 * 繰り返し支払いの支払日・計上日の算出(旧: 固定費の計上日・支払日)。
 *
 * 2026-09-08 改修: 支払日が基準。
 *   毎月: 支払日(1〜31 / 末日)。計上日は「支払日の前々月〜翌々月」＋日にち(1〜31 / 末日)
 *         例: 7月分の家賃を8月5日に払う → 支払日 5日 / 計上日 前月の末日 → PLは7月、CFは8月
 *   毎週: 指定した曜日ごとに1回(計上日=支払日)
 *   クレカ(2026-09-09): 計上日(PL)の日にちがカードの利用日。CF(支払日)はカードの締め日・引き落とし日から自動で決まり、
 *         土日祝のずらし方もカードの設定に従う(支払日欄・計上日の月の指定は使わない)
 * 支払日が土日祝のときは、設定に応じて前の平日・後の平日へずらす(2026-09-07 指示)。
 * 指定日がその月に存在しない場合(例: 31日で2月)は、その月の末日に丸める。
 * 旧データ(対象月基準: 計上日 末日/指定日・支払日 当月/翌月/翌々月)は同じ意味になるよう読み替える。
 */
import { addDays, endOfMonth } from '@core/lib/date';
import { weekendOrHoliday } from '@core/lib/jpHolidays';
import { debitDateFor, hasDebitRule } from '@core/lib/cardDebit';
import type {
  CashAccount,
  DateStr,
  DayOrEnd,
  HolidayShift,
  MonthStr,
  PaymentRule,
  PostingRule,
  RecurringCost,
  RecurringFrequency,
} from '@core/lib/types';

export const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'] as const;
/** 曜日の選択肢(月〜日の順。値は Date#getDay と同じ 0=日〜6=土) */
export const WEEKDAY_OPTIONS: Array<{ value: number; label: string }> = [1, 2, 3, 4, 5, 6, 0].map((d) => ({
  value: d,
  label: `${WEEKDAY_LABELS[d]}曜日`,
}));
export type PostingOffset = -2 | -1 | 0 | 1 | 2;
/** 計上日の月(支払日に対する相対) */
export const POSTING_OFFSET_OPTIONS: Array<{ value: PostingOffset; label: string }> = [
  { value: -2, label: '支払日の前々月' },
  { value: -1, label: '支払日の前月' },
  { value: 0, label: '支払日の当月' },
  { value: 1, label: '支払日の翌月' },
  { value: 2, label: '支払日の翌々月' },
];

export function addMonths(month: MonthStr, n: number): MonthStr {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** 'YYYY-MM' の翌月 */
export function nextMonthOf(month: MonthStr): MonthStr {
  return addMonths(month, 1);
}

/** その月の指定日。'end' と存在しない日付は末日に丸める */
function dayOf(month: MonthStr, day: DayOrEnd): DateStr {
  if (day === 'end') return endOfMonth(month);
  const last = Number(endOfMonth(month).slice(8, 10));
  const safe = Math.min(Math.max(day, 1), last);
  return `${month}-${String(safe).padStart(2, '0')}`;
}

function weekdayOf(date: DateStr): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** 支払日基準に正規化したルール */
export type NormalizedRules = {
  /** 毎月の支払日 */
  payDay: DayOrEnd;
  /** 計上日の月(支払日の月からのずれ) */
  postOffset: PostingOffset;
  /** 計上日の日にち */
  postDay: DayOrEnd;
};

/** 新旧どちらのルールでも、支払日基準の形にそろえる */
export function normalizeRules(cost: Pick<RecurringCost, 'payment_rule' | 'posting_rule'>): NormalizedRules {
  const pr = cost.payment_rule;
  const po = cost.posting_rule;
  if (pr.kind === 'day_of_month') {
    if (po.kind === 'relative') return { payDay: pr.day, postOffset: po.month_offset, postDay: po.day };
    return { payDay: pr.day, postOffset: 0, postDay: po.kind === 'month_end' ? 'end' : po.day };
  }
  // 旧データ: 対象月Mに計上し、支払いは M+0/1/2 月。支払日基準では計上月 = 支払月 − ずれ
  const payOffset = pr.kind === 'month_end' || pr.kind === 'same_month' ? 0 : pr.kind === 'next_month' ? 1 : 2;
  const payDay: DayOrEnd = pr.kind === 'month_end' ? 'end' : pr.day;
  const postDay: DayOrEnd = po.kind === 'month_end' ? 'end' : po.day;
  return { payDay, postOffset: -payOffset as PostingOffset, postDay };
}

export function frequencyOf(cost: Pick<RecurringCost, 'frequency'>): RecurringFrequency {
  return cost.frequency ?? { kind: 'monthly' };
}

export function holidayShiftOf(cost: Pick<RecurringCost, 'holiday_shift'>): HolidayShift {
  return cost.holiday_shift ?? 'after';
}

/** 支払日が土日祝なら、前の平日または後の平日へずらす(連休でも最大14日まで探す) */
export function shiftForHoliday(date: DateStr, shift: HolidayShift): DateStr {
  if (shift === 'none') return date;
  let d = date;
  for (let i = 0; i < 14 && weekendOrHoliday(d); i++) d = addDays(d, shift === 'before' ? -1 : 1);
  return d;
}

/** 1回分の支払い。key は取引IDに使う(毎月=支払う月 / 毎週=本来の支払日) */
export type Occurrence = {
  key: string;
  /** 設定どおりの支払日(ずらす前) */
  scheduledDate: DateStr;
  /** 土日祝をずらした実際の支払日(CF) */
  paymentDate: DateStr;
  /** 計上日(PL) */
  postingDate: DateStr;
  /** クレカのみ: カードの利用日(支払日欄で指定した日)。引き落とし日の計算元 */
  usageDate?: DateStr;
};

/**
 * 支払う月(payMonth)に発生する回。毎月=1回、毎週=その月の該当曜日ごと。
 * クレカは account(カード)を渡すと、利用日から引き落とし日(CF)を計算する(設定がなければ利用日のまま)
 */
export function occurrencesFor(cost: RecurringCost, payMonth: MonthStr, account?: CashAccount | null): Occurrence[] {
  const freq = frequencyOf(cost);
  const shift = holidayShiftOf(cost);
  const card = cost.payment_method === 'card';
  const debitOf = (usage: DateStr): DateStr =>
    card && account && hasDebitRule(account) ? (debitDateFor(usage, account) ?? usage) : usage;
  if (freq.kind === 'monthly') {
    const r = normalizeRules(cost);
    const scheduled = dayOf(payMonth, r.payDay);
    if (card) {
      // クレカ: 計上日(PL)の日にち = カードの利用日。引き落とし日(CF)はカードの設定から
      const usage = dayOf(payMonth, r.postDay);
      return [{ key: payMonth, scheduledDate: usage, usageDate: usage, paymentDate: debitOf(usage), postingDate: usage }];
    }
    return [
      {
        key: payMonth,
        scheduledDate: scheduled,
        paymentDate: shiftForHoliday(scheduled, shift),
        postingDate: dayOf(addMonths(payMonth, r.postOffset), r.postDay),
      },
    ];
  }
  const out: Occurrence[] = [];
  const last = Number(endOfMonth(payMonth).slice(8, 10));
  for (let day = 1; day <= last; day++) {
    const d = `${payMonth}-${String(day).padStart(2, '0')}`;
    if (weekdayOf(d) !== freq.weekday) continue;
    if (card) {
      // クレカ×毎週: 利用日=計上日、引き落とし日はカードの設定から
      out.push({ key: d, scheduledDate: d, usageDate: d, paymentDate: debitOf(d), postingDate: d });
      continue;
    }
    const paymentDate = shiftForHoliday(d, shift);
    // 毎週の分は計上日=支払日(週ごとの費用として支払った月のPLへ)
    out.push({ key: d, scheduledDate: d, paymentDate, postingDate: paymentDate });
  }
  return out;
}

export const dayLabel = (d: DayOrEnd): string => (d === 'end' ? '末日' : `${d}日`);
const offsetLabel = (o: number) => POSTING_OFFSET_OPTIONS.find((x) => x.value === o)?.label ?? '支払日の当月';

/** 一覧用(支払日): 「毎月5日」「毎週 月曜日」。クレカは引き落とし日がカードの設定で決まる */
export function describePaymentDay(cost: RecurringCost): string {
  const freq = frequencyOf(cost);
  if (cost.payment_method === 'card') return 'カードの引き落とし日(自動)';
  if (freq.kind === 'weekly') return `毎週 ${WEEKDAY_LABELS[freq.weekday] ?? '?'}曜日`;
  return `毎月${dayLabel(normalizeRules(cost).payDay)}`;
}

/** 一覧用(計上日): 「支払日の前月の末日」「支払日と同じ日」。クレカは「毎月15日」(利用日=計上日) */
export function describePostingDay(cost: RecurringCost): string {
  const freq = frequencyOf(cost);
  if (cost.payment_method === 'card') {
    return freq.kind === 'weekly' ? `毎週 ${WEEKDAY_LABELS[freq.weekday] ?? '?'}曜日` : `毎月${dayLabel(normalizeRules(cost).postDay)}`;
  }
  if (freq.kind === 'weekly') return '支払日と同じ日';
  const r = normalizeRules(cost);
  return `${offsetLabel(r.postOffset)}の${dayLabel(r.postDay)}`;
}

/** 「毎月5日支払・計上は支払日の前月の末日」「毎週 月曜日」(お知らせ等で1行にしたいとき) */
export function describeFrequency(cost: RecurringCost): string {
  const freq = frequencyOf(cost);
  if (freq.kind === 'weekly') return `毎週 ${WEEKDAY_LABELS[freq.weekday] ?? '?'}曜日(計上日=支払日)`;
  const r = normalizeRules(cost);
  if (cost.payment_method === 'card') {
    return `毎月${dayLabel(r.postDay)}に計上(カード利用)・引き落としはカードの設定どおり`;
  }
  return `毎月${dayLabel(r.payDay)}支払・計上は${offsetLabel(r.postOffset)}の${dayLabel(r.postDay)}`;
}

export function describeHolidayShift(shift: HolidayShift): string {
  if (shift === 'before') return '土日祝は前の平日';
  if (shift === 'after') return '土日祝は後の平日';
  return '土日祝もそのまま';
}

/** 互換のため残す(旧データの表示に使っていた) */
export function describeRules(posting: PostingRule, payment: PaymentRule): string {
  return describeFrequency({ posting_rule: posting, payment_rule: payment, frequency: { kind: 'monthly' } } as RecurringCost);
}
